import { useState } from "react";
import { Button } from "@mui/material";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import IndustrialExperienceDropdown from "./IndustrialExperienceDropdown";
import ErrorList from "./ErrorList";
import { useTalent } from "../api/talent";
import TalentIndustrialExperienceChips from "./TalentIndustrialExperienceChips";
import FormCard from "./FormCard";

export type TalentIndustrialExperienceFormValues = {
    industrialExperienceId: string;
};

export const validationSchemaFields = {
    industrialExperienceId: yup.string().required("Industry is required."),
};

const schema = yup.object(validationSchemaFields);

export const useTalentIndustrialExperienceForm = () => {
    return useForm<TalentIndustrialExperienceFormValues>({
        resolver: yupResolver(schema),
    });
};

export type TalentIndustrialExperiencesFormProps = {
    talentId: string
}

const TalentIndustrialExperiencesForm = ({ talentId }: TalentIndustrialExperiencesFormProps) => {
    const [errors, setErrors] = useState<string[]>([]);
    const methods = useTalentIndustrialExperienceForm();
    const {
        talent,
        adminAddTalentIndustrialExperience,
        isAdminAddingTalentIndustrialExperience,
        adminRemoveTalentIndustrialExperience,
        isAdminRemovingTalentIndustrialExperience,
    } = useTalent(talentId);

    const handleSubmit = async (values: TalentIndustrialExperienceFormValues) => {
        setErrors([]);

        const existingIndustrialExperience = talent?.industrialExperiences?.find(
            industrialExperience => industrialExperience.industrialExperienceId === values.industrialExperienceId
        );

        if (existingIndustrialExperience) {
            setErrors([`Industry "${existingIndustrialExperience.industrialExperienceName}" already added.`]);
            methods.reset();
            return;
        }

        const response = await adminAddTalentIndustrialExperience(values.industrialExperienceId);

        if (!response.success) {
            setErrors(response.error.userMessages);
        } else {
            methods.reset();
        }
    };

    return (
        <div>
            <FormCard title="Industrial experience" withoutTitleUnderline width="100%">
                <form
                    noValidate
                    className="flex gap-6 flex-wrap md:flex-nowrap md:items-start"
                    onSubmit={methods.handleSubmit(handleSubmit)}
                >
                    <div className="flex gap-6 flex-wrap w-full md:flex-nowrap">
                        <div className="w-full">
                            <Controller
                                name="industrialExperienceId"
                                control={methods.control}
                                render={({ field: { onChange, value } }) => (
                                    <IndustrialExperienceDropdown
                                        required
                                        label="Select industry"
                                        error={methods.formState.errors.industrialExperienceId}
                                        onChange={onChange}
                                        value={value}
                                        disabled={isAdminAddingTalentIndustrialExperience}
                                    />
                                )}
                            />
                        </div>
                    </div>
                    <Button
                        color="secondary"
                        variant="outlined"
                        className="w-full md:mt-auto md:w-auto md:self-end"
                        disabled={isAdminAddingTalentIndustrialExperience}
                        type="submit"
                    >
                        Add
                    </Button>
                </form>

                <ErrorList errors={errors} />

                <TalentIndustrialExperienceChips
                    industrialExperiences={talent?.industrialExperiences}
                    removeIndustrialExperience={adminRemoveTalentIndustrialExperience}
                    isRemovingIndustrialExperience={isAdminRemovingTalentIndustrialExperience}
                />
            </FormCard>
        </div>
    );
};

export default TalentIndustrialExperiencesForm;
